import { useEffect, useRef, useState } from "react";

/**
 * Lightweight hero slideshow — pure CSS crossfade + Ken Burns (images) OR native video slides.
 * - No framer-motion (fast, GPU-cheap).
 * - Slides can mix { src, poster, kind: "video"|"image", label, alt }.
 * - If a custom `videoUrl` prop is given, it overrides slides (admin-uploaded single video).
 */
export default function HeroSlideshow({ slides, videoUrl, interval = 6500 }) {
  const [index, setIndex] = useState(0);
  const videoRefs = useRef({});

  // Auto-advance (skip if a single admin video is used)
  useEffect(() => {
    if (videoUrl) return;
    if (!slides || slides.length < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), interval);
    return () => clearInterval(id);
  }, [slides, interval, videoUrl]);

  // Restart the current video from 0 whenever the slide changes
  useEffect(() => {
    const el = videoRefs.current[index];
    if (el) { try { el.currentTime = 0; el.play().catch(() => {}); } catch (_) {} }
  }, [index]);

  // Admin-provided video override
  if (videoUrl) {
    return (
      <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem] md:aspect-[5/6]" data-testid="hero-media">
        <video className="h-full w-full object-cover" src={videoUrl} autoPlay muted loop playsInline preload="metadata" data-testid="hero-video" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
        <span className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-ink backdrop-blur-sm">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lime-dim" /> Ambiance live
        </span>
      </div>
    );
  }

  const current = slides?.[index];
  return (
    <div className="relative aspect-[4/5] overflow-hidden rounded-[1.5rem] md:aspect-[5/6]" data-testid="hero-media">
      {slides?.map((s, i) => {
        const isVideo = s.kind === "video";
        const active = i === index;
        const base = `absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ease-out ${active ? "opacity-100" : "opacity-0"}`;
        if (isVideo) {
          return (
            <video
              key={s.src}
              ref={(el) => { videoRefs.current[i] = el; }}
              className={base}
              poster={s.poster}
              autoPlay={active}
              muted
              loop
              playsInline
              preload={i === 0 ? "auto" : "none"}
              data-testid={`hero-slide-${i}`}
            >
              <source src={s.src} type="video/mp4" />
              {s.webm && <source src={s.webm} type="video/webm" />}
            </video>
          );
        }
        return (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            loading={i === 0 ? "eager" : "lazy"}
            className={`${base} ${active ? "hero-kenburns" : ""}`}
            data-testid={`hero-slide-${i}`}
          />
        );
      })}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

      {/* Live label + slide caption */}
      <div className="pointer-events-none absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
        <span className="flex items-center gap-2 rounded-full bg-white/85 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-ink backdrop-blur-sm">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lime-dim" /> En coulisses
        </span>
        <span
          key={current?.label}
          className="rounded-full bg-ink/70 px-3 py-1.5 text-[11px] font-medium uppercase tracking-[0.15em] text-white backdrop-blur-sm hero-caption-fade"
          data-testid="hero-slide-caption"
        >
          {current?.label}
        </span>
      </div>

      {/* Dots */}
      {slides && slides.length > 1 && (
        <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === index ? "w-8 bg-white" : "w-1.5 bg-white/60 hover:bg-white/90"
              }`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
